import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * HardcodedValueWarning - Displays a warning banner when hardcoded business values are detected
 * Only shows in development mode to alert developers about hardcoded values
 */
const HardcodedValueWarning = ({ hardcodedValues = [], onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Only show in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (isDevelopment && hardcodedValues.length > 0 && !isDismissed) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isDevelopment, hardcodedValues.length, isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!isDevelopment || !isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -100 }}
          className="fixed top-0 left-0 right-0 z-50 bg-red-600 border-b-2 border-red-700 shadow-lg"
        >
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-300 flex-shrink-0" />
                <div>
                  <h3 className="text-white font-semibold text-sm">
                    ⚠️ HÅRDKODADE AFFÄRSVÄRDEN UPPTÄCKTA
                  </h3>
                  <p className="text-red-100 text-xs mt-1">
                    Detta system använder hårdkodade värden som borde komma från system_settings
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleDismiss}
                className="text-red-200 hover:text-white transition-colors p-1"
                aria-label="Stäng varning"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {hardcodedValues.length > 0 && (
              <div className="mt-3 pl-9">
                <details className="text-red-100">
                  <summary className="cursor-pointer text-xs hover:text-white">
                    Visa hårdkodade värden ({hardcodedValues.length})
                  </summary>
                  <div className="mt-2 bg-red-700/30 rounded p-2 max-h-32 overflow-y-auto">
                    {hardcodedValues.map((item, index) => (
                      <div key={index} className="text-xs mb-1">
                        <span className="font-mono text-yellow-300">{item.value}</span>
                        <span className="text-red-200"> i </span>
                        <span className="text-red-100">{item.location}</span>
                        {item.shouldBe && (
                          <span className="text-red-200"> → använd: </span>
                        )}
                        {item.shouldBe && (
                          <span className="font-mono text-green-300">{item.shouldBe}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
            
            <div className="mt-2 pl-9">
              <p className="text-red-100 text-xs">
                💡 Alla affärsvärden bör lagras i <span className="font-mono bg-red-700/50 px-1 rounded">system_settings</span> tabellen
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Hook for detecting and managing hardcoded values
 */
export const useHardcodedValueDetector = () => {
  const [hardcodedValues, setHardcodedValues] = useState([]);

  const addHardcodedValue = (value, location, shouldBe = null) => {
    setHardcodedValues(prev => {
      const exists = prev.some(item => item.value === value && item.location === location);
      if (!exists) {
        return [...prev, { value, location, shouldBe, timestamp: Date.now() }];
      }
      return prev;
    });
  };

  const clearHardcodedValues = () => {
    setHardcodedValues([]);
  };

  return { hardcodedValues, addHardcodedValue, clearHardcodedValues };
};

/**
 * Development helper function to detect potential hardcoded values
 */
export const detectHardcodedValue = (value, fallback, location) => {
  if (process.env.NODE_ENV === 'development') {
    if (value === fallback) {
      console.warn(`🚨 Potential hardcoded value detected: ${value} in ${location}`);
      return true;
    }
  }
  return false;
};

export default HardcodedValueWarning;